import type { ComponentProps } from "@chatbotgang/etude/emotion-react/ComponentProps";
import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Badge } from "@/components/Badge";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Select } from "@/components/Select";
import { Typography } from "@/components/Typography";
import { useEnableEmailBroadcast } from "@/features/email/useEnableEmailBroadcast";
import { Trans } from "@/features/i18n/Trans";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ChannelNameEdit } from "@/pages/channels/pages/detail/components/ChannelNameEdit";
import { EmailSenderConfigurationTab } from "@/pages/channels/pages/detail/email/EmailSenderConfigurationTab";
import { EmailSendingStatusTab } from "@/pages/channels/pages/detail/email/EmailSendingStatusTab";

const TabsSchema = z.enum(["sendingStatus", "senderConfiguration"]);
type TabsSchemaType = z.infer<typeof TabsSchema>;

type EmailChannel = InterludeTypes["EmailChannel"];

const styles = {
  infoBox: {
    maxWidth: "776px",
  },
  section: {
    marginTop: "32px",
  },
  sectionTitle: {
    marginBottom: "16px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const BrandDomainSection: React.FC<{
  externalChannelId: string | null;
  hideTitle?: boolean;
}> = function BrandDomainSection({ externalChannelId, hideTitle }) {
  const { t } = useTranslation();
  return (
    <Stack spacing="8px">
      {hideTitle ? null : (
        <Typography variant="body" fontWeight={600}>
          {t("channels.email.detail.section.brandDomain")}
        </Typography>
      )}
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {t("channels.email.detail.section.brandDomain.desc")}
      </Typography>
      <Box
        sx={{
          ...styles.infoBox,
          padding: "12px 16px",
          backgroundColor: theme.colors.staticBgContainer,
          borderLeft: `2px solid ${theme.colors.staticFgPlaceholder}`,
        }}
      >
        <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
          <Badge variant="plain">{t("channels.email.label.brandDomain")}</Badge>
          <Typography variant="body" fontWeight={500}>
            {externalChannelId ?? "—"}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

interface TabPanelProps {
  index: TabsSchemaType;
  value: TabsSchemaType;
  children?: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
}) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`email-info-tabpanel-${index}`}
      aria-labelledby={`email-info-tab-${index}`}
    >
      {value === index ? children : null}
    </Box>
  );
};

interface EmailInfoTabProps {
  channel: Pick<EmailChannel, "id" | "externalChannelId"> &
    Partial<Pick<EmailChannel, "status">>;
}

export const EmailInfoTab: React.FC<EmailInfoTabProps> = function EmailInfoTab({
  channel,
}) {
  const { t } = useTranslation();
  const senderDomainsQuery = interlude.channel.useGetChannelSenderDomains({
    params: { channelId: channel.id },
  });
  const senderProfilesQuery = interlude.channel.useGetChannelSenderProfiles({
    params: { channelId: channel.id },
  });
  const enableEmailBroadcast = useEnableEmailBroadcast();
  const [tab, setTab] = useState<TabsSchemaType>(TabsSchema.Enum.sendingStatus);
  const handleTabChange = useHandler<ComponentProps<typeof Tabs>["onChange"]>(
    (_, newValue) => {
      const result = TabsSchema.safeParse(newValue);
      if (!result.success) return;
      setTab(result.data);
    },
  );

  if (senderDomainsQuery.isLoading || senderProfilesQuery.isLoading) {
    return <BarLoading />;
  }

  const senderDomains = senderDomainsQuery.data ?? [];
  const senderProfiles = senderProfilesQuery.data ?? [];

  return (
    <Stack gap="8px">
      <Box sx={styles.section}>
        <Box sx={styles.sectionTitle}>
          <Typography variant="h3" fontWeight={600}>
            {t("channels.email.detail.section.connectedEmail")}
          </Typography>
        </Box>
        <ChannelNameEdit />
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionTitle}>
          <Typography variant="h3" fontWeight={600}>
            {t("channels.email.detail.section.generalSettings")}
          </Typography>
        </Box>
        <Stack spacing="8px">
          <Typography variant="body" fontWeight={600}>
            {t("channels.email.detail.section.unsubscribeSetting")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("channels.email.detail.section.unsubscribeSetting.desc")}
          </Typography>
          <div style={{ width: "300px" }}>
            <Select
              disabled
              value="MAAC-hosted page"
              options={[
                {
                  label: t(
                    "channels.email.detail.section.unsubscribeSetting.maacHostedPage",
                  ),
                  value: "MAAC-hosted page",
                },
              ]}
            />
          </div>
        </Stack>
      </Box>

      {enableEmailBroadcast && channel.status ? (
        <>
          <Box sx={styles.section}>
            <Box sx={styles.sectionTitle}>
              <Typography variant="h3" fontWeight={600}>
                <Trans i18nKey="channels.email.detail.section.brandDomain.heading" />
              </Typography>
            </Box>
            <BrandDomainSection
              externalChannelId={channel.externalChannelId}
              hideTitle
            />
          </Box>

          <Box sx={styles.section}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab
                label={
                  <Trans i18nKey="channels.email.detail.tabs.sendingStatus" />
                }
                value={TabsSchema.Values.sendingStatus}
              />
              <Tab
                label={
                  <Trans i18nKey="channels.email.detail.tabs.senderConfiguration" />
                }
                value={TabsSchema.Values.senderConfiguration}
              />
            </Tabs>
            <TabPanel value={tab} index={TabsSchema.Enum.sendingStatus}>
              <EmailSendingStatusTab
                channelId={channel.id}
                channelStatus={channel.status}
                senderDomain={senderDomains[0]?.domain ?? ""}
              />
            </TabPanel>
            <TabPanel value={tab} index={TabsSchema.Enum.senderConfiguration}>
              <EmailSenderConfigurationTab
                channelId={channel.id}
                senderDomains={senderDomains}
                senderProfiles={senderProfiles}
              />
            </TabPanel>
          </Box>
        </>
      ) : (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle}>
            <Typography variant="h3" fontWeight={600}>
              {t("channels.email.detail.section.senderSetting")}
            </Typography>
          </Box>
          <Stack spacing="16px">
            <BrandDomainSection externalChannelId={channel.externalChannelId} />
            <EmailSenderConfigurationTab
              channelId={channel.id}
              senderDomains={senderDomains}
              senderProfiles={senderProfiles}
            />
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
